// Atlas Phase 4 — detected-quality verdict (pure). Spec §5.1 / §6 #2.
//
// Weighs OBSERVED res × bitrate × fps × codec into one verdict + per-metric
// colour levels. fps is weighted AS MUCH as resolution because for sport a
// 720p60 stream beats a 1080p25 one. Thresholds are PROVISIONAL (spec §6 #2 —
// tune against real streams; likely differ by content type) and live in ONE
// tunables object. Nulls are excluded and the remaining weights renormalised,
// so a partial reading still scores; everything-null → verdict null ("Detecting…").

export type StatusLevel = 'good' | 'warn' | 'bad';
export type Verdict = 'excellent' | 'good' | 'ok' | 'poor';

export interface QualityMetrics {
    height?: number | null;
    bitrateBps?: number | null;
    fps?: number | null;
    videoCodec?: string | null;
}

export interface QualityVerdict {
    verdict: Verdict | null;
    score: number | null; // 0..1, null when nothing measured
    levels: {
        res: StatusLevel | null;
        bitrate: StatusLevel | null;
        fps: StatusLevel | null;
        codec: StatusLevel | null;
    };
}

export const QUALITY_TUNABLES = {
    // Per-dimension 0..1 sub-scores (step functions: [threshold, score], descending).
    RES: [
        [2160, 1.0],
        [1080, 0.85],
        [720, 0.65],
        [576, 0.45],
        [480, 0.35],
        [0, 0.2],
    ] as const,
    BITRATE: [
        [8_000_000, 1.0],
        [5_000_000, 0.85],
        [3_000_000, 0.65],
        [1_500_000, 0.45],
        [0, 0.25],
    ] as const,
    FPS: [
        [50, 1.0],
        [30, 0.7],
        [24, 0.5],
        [0, 0.3],
    ] as const,
    // Modern codecs deliver more quality per bit.
    CODEC: { hevc: 1.0, h265: 1.0, av1: 1.0, vp9: 0.95, h264: 0.8, avc: 0.8 } as Record<string, number>,
    CODEC_DEFAULT: 0.5,
    // Codec coding efficiency vs H.264 — bitrate is scored on the EFFECTIVE
    // (H.264-equivalent) rate: 5 Mbps HEVC carries ≈ 8 Mbps H.264 of picture.
    // Literature: ~30–50% savings for HEVC (lower end for live/fast hardware
    // encoders, which is what IPTV transcoders run); AV1 a step beyond.
    // Provisional tunables (spec §6 #2).
    CODEC_EFFICIENCY: { hevc: 1.6, h265: 1.6, av1: 1.9, vp9: 1.3, h264: 1.0, avc: 1.0 } as Record<string, number>,
    CODEC_EFFICIENCY_DEFAULT: 1.0,
    // fps weighted == res (sport). Codec is a minor nudge.
    WEIGHTS: { res: 0.3, bitrate: 0.3, fps: 0.3, codec: 0.1 },
    // Verdict cutoffs on the weighted score.
    VERDICT: [
        [0.8, 'excellent'],
        [0.6, 'good'],
        [0.4, 'ok'],
        [0, 'poor'],
    ] as const,
    // Per-metric colour thresholds (good/warn boundaries; below warn → bad).
    BITRATE_GOOD: 5_000_000,
    BITRATE_WARN: 2_500_000,
    FPS_GOOD: 50,
    FPS_WARN: 25,
    RES_GOOD: 1080,
    RES_WARN: 576,
};

function stepScore(value: number, table: readonly (readonly [number, number])[]): number {
    for (const [threshold, score] of table) {
        if (value >= threshold) return score;
    }
    return table[table.length - 1]![1];
}

/**
 * Fold producer codec spellings to the tunables' canonical keys — "h.264"
 * (hls.js stats path), "h264" (probe), RFC6381 strings ("avc1.64001f",
 * "hvc1.1.6.L120") all land on one key. A spelling miss must never fall
 * to the unknown-codec penalty.
 */
export function normalizeVideoCodec(codec: string): string {
    let c = codec.toLowerCase().replace(/\./g, '');
    if (c.startsWith('avc')) return 'avc';
    if (c.startsWith('hvc') || c.startsWith('hev')) return 'hevc';
    if (c.startsWith('h264')) return 'h264';
    if (c.startsWith('h265')) return 'h265';
    return c;
}

/** Coding-efficiency factor vs H.264 for the given codec (1.0 when unknown). */
export function codecBitrateEfficiency(codec: string | null | undefined, tunables = QUALITY_TUNABLES): number {
    if (typeof codec !== 'string' || !codec) return tunables.CODEC_EFFICIENCY_DEFAULT;
    return tunables.CODEC_EFFICIENCY[normalizeVideoCodec(codec)] ?? tunables.CODEC_EFFICIENCY_DEFAULT;
}

function level(value: number, good: number, warn: number): StatusLevel {
    if (value >= good) return 'good';
    if (value >= warn) return 'warn';
    return 'bad';
}

export function scoreQuality(m: QualityMetrics, tunables = QUALITY_TUNABLES): QualityVerdict {
    const parts: { weight: number; score: number }[] = [];
    const levels: QualityVerdict['levels'] = { res: null, bitrate: null, fps: null, codec: null };
    const hasBitrate = typeof m.bitrateBps === 'number' && m.bitrateBps > 0;

    if (typeof m.height === 'number' && m.height > 0) {
        parts.push({ weight: tunables.WEIGHTS.res, score: stepScore(m.height, tunables.RES) });
        levels.res = level(m.height, tunables.RES_GOOD, tunables.RES_WARN);
    }
    if (hasBitrate) {
        // Score + colour the EFFECTIVE (H.264-equivalent) rate: 5 Mbps HEVC
        // carries ≈ 8 Mbps H.264 of picture, so it must not score (or read
        // amber) as if it were a thin H.264 stream.
        const effective = (m.bitrateBps as number) * codecBitrateEfficiency(m.videoCodec, tunables);
        parts.push({ weight: tunables.WEIGHTS.bitrate, score: stepScore(effective, tunables.BITRATE) });
        levels.bitrate = level(effective, tunables.BITRATE_GOOD, tunables.BITRATE_WARN);
    }
    if (typeof m.fps === 'number' && m.fps > 0) {
        parts.push({ weight: tunables.WEIGHTS.fps, score: stepScore(m.fps, tunables.FPS) });
        levels.fps = level(m.fps, tunables.FPS_GOOD, tunables.FPS_WARN);
    }
    if (typeof m.videoCodec === 'string' && m.videoCodec) {
        // Spellings are folded so "h.264"/"avc1.64001f" never land on the
        // unknown-codec penalty (field case: an 8 Mbps h.264 stream ranked
        // below 5 Mbps streams because "h.264" missed the map).
        const cs = tunables.CODEC[normalizeVideoCodec(m.videoCodec)] ?? tunables.CODEC_DEFAULT;
        // With a measured bitrate the codec's quality-per-bit advantage is
        // already priced into the effective rate above — scoring it again
        // would double-count. The codec term is a weak proxy used only when
        // bitrate is unknown. The display level is always computed.
        if (!hasBitrate) {
            parts.push({ weight: tunables.WEIGHTS.codec, score: cs });
        }
        levels.codec = cs >= 0.9 ? 'good' : cs >= 0.7 ? 'warn' : 'bad';
    }

    if (parts.length === 0) return { verdict: null, score: null, levels };

    const totalWeight = parts.reduce((s, p) => s + p.weight, 0);
    const score = parts.reduce((s, p) => s + p.weight * p.score, 0) / totalWeight;

    let verdict: Verdict = 'poor';
    for (const [cutoff, v] of tunables.VERDICT) {
        if (score >= cutoff) {
            verdict = v;
            break;
        }
    }
    return { verdict, score, levels };
}
